import { useEffect, useRef, useState } from 'react';
import { FINGER_KEY_MAPPING } from '@/data/fingerKeyMapping';

// Declare THREE for TypeScript compilation without imports
declare const THREE: any;

export default function GuitarHeroGame() {
    const mountRef = useRef<HTMLDivElement>(null);
    const [uiState, setUiState] = useState({ score: 0, combo: 0 });
    const [isLoaded, setIsLoaded] = useState(false);
    const [isMusicLoaded, setIsMusicLoaded] = useState(false);
    const [gameState, setGameState] = useState<'menu' | 'playing'>('menu');

    // Game Configuration States
    const [handMode, setHandMode] = useState<'left' | 'right' | 'both'>('both');
    const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');

    // 1. Inject Three.js and GLTFLoader cleanly
    useEffect(() => {
        const loadScripts = async () => {
            if (!(window as any).THREE) {
                await new Promise((resolve) => {
                    const script = document.createElement('script');
                    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
                    script.onload = resolve;
                    document.body.appendChild(script);
                });
            }
            if (!(window as any).THREE.GLTFLoader) {
                await new Promise((resolve) => {
                    const script = document.createElement('script');
                    script.src = 'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/GLTFLoader.js';
                    script.onload = resolve;
                    document.body.appendChild(script);
                });
            }
            setIsLoaded(true);
        };
        loadScripts();
    }, []);

    // 2. Initialize Game Engine
    useEffect(() => {
        if (!isLoaded || !mountRef.current) return;

        mountRef.current.innerHTML = '';

        // --- GAME STATE ---
        let score = 0;
        let combo = 0;
        let isPlaying = true;
        let gameActive = false;
        const tiles: any[] = [];
        const floatingTexts: any[] = [];
        const particles: any[] = [];
        const animatedCubes: any[] = [];

        // --- DYNAMIC ENGINE PARAMETERS ---
        let currentLanes = 6;
        let currentTrackWidth = 22;
        let currentFallSpeed = 18;
        let currentSpawnCooldown = 0.38;
        let currentSpawnEnergyThreshold = 180;
        let currentMode: 'left' | 'right' | 'both' = 'both';

        // --- TRUE BUFFERED AUDIO ENGINE ---
        const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
        const audioCtx = new AudioContext();

        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 64;
        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        let audioBuffer: any = null;
        let audioSourceNode: any = null;

        fetch('https://raw.githubusercontent.com/mdn/webaudio-examples/master/audio-analyser/viper.mp3')
            .then(response => response.arrayBuffer())
            .then(arrayBuffer => audioCtx.decodeAudioData(arrayBuffer))
            .then(decodedAudio => {
                audioBuffer = decodedAudio;
                setIsMusicLoaded(true);
            })
            .catch(err => {
                console.error("Audio failed to load", err);
                setIsMusicLoaded(true);
            });


        const startMusic = () => {
            if (audioCtx.state === 'suspended') audioCtx.resume();

            if (audioBuffer && !audioSourceNode) {
                audioSourceNode = audioCtx.createBufferSource();
                audioSourceNode.buffer = audioBuffer;
                audioSourceNode.loop = true;

                audioSourceNode.connect(analyser);
                analyser.connect(audioCtx.destination);

                audioSourceNode.start(0);
            }
        };

        const startGameHandler = (e: any) => {
            if (e.detail) {
                const { handMode: mode, difficulty: diff } = e.detail;

                if (diff === 'easy') {
                    currentFallSpeed = 12;
                    currentSpawnCooldown = 0.65;
                    currentSpawnEnergyThreshold = 205;
                } else if (diff === 'medium') {
                    currentFallSpeed = 18;
                    currentSpawnCooldown = 0.38;
                    currentSpawnEnergyThreshold = 180;
                } else if (diff === 'hard') {
                    currentFallSpeed = 28;
                    currentSpawnCooldown = 0.22;
                    currentSpawnEnergyThreshold = 150;
                }

                if (mode === 'left') {
                    currentLanes = 5;
                    currentTrackWidth = 18;
                } else if (mode === 'right') {
                    currentLanes = 5;
                    currentTrackWidth = 18;
                } else {
                    currentLanes = 10;
                    currentTrackWidth = 34;
                }
                currentMode = mode;

                const scale = currentTrackWidth / 22;
                floor.scale.x = scale;
                hitLine.scale.x = scale;

                leftCubesGroup.position.x = -currentTrackWidth / 2 - 1.2;
                rightCubesGroup.position.x = currentTrackWidth / 2 + 1.2;

                camera.position.y = mode === 'both' ? 14 : 10;
            }

            gameActive = true;
            startMusic();
        };

        const stopGameHandler = () => {
            gameActive = false;
            if (audioSourceNode) {
                try { audioSourceNode.stop(); } catch (e) { }
                audioSourceNode.disconnect();
                audioSourceNode = null;
            }
            tiles.forEach(t => scene.remove(t.mesh));
            tiles.length = 0;
            score = 0;
            combo = 0;
            setUiState({ score, combo });
        };

        window.addEventListener('START_GAME', startGameHandler);
        window.addEventListener('STOP_GAME', stopGameHandler);

        function playTone(freq: number, type: string, duration: number, vol = 0.1) {
            try {
                if (audioCtx.state === 'suspended') audioCtx.resume();
                const osc = audioCtx.createOscillator();
                const gain = audioCtx.createGain();
                osc.type = type as any;
                osc.frequency.setValueAtTime(freq, audioCtx.currentTime);
                gain.gain.setValueAtTime(vol, audioCtx.currentTime);
                gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + duration);
                osc.connect(gain);
                gain.connect(audioCtx.destination);
                osc.start();
                osc.stop(audioCtx.currentTime + duration);
            } catch (e) { }
        }

        // --- THREE.JS SETUP ---
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0x0a0014);
        scene.fog = new THREE.FogExp2(0x0a0014, 0.005);

        const camera = new THREE.PerspectiveCamera(55, window.innerWidth / (window.innerHeight - 100), 0.1, 1000);
        camera.position.set(0, 10, 20);
        camera.lookAt(0, 0, -20);

        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
        renderer.setSize(window.innerWidth, window.innerHeight - 100);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        mountRef.current.appendChild(renderer.domElement);

        // --- ENVIRONMENT OBJECTS & LIGHTING ---
        const HIT_Z = 6;
        const SPAWN_Z = -160; // Spawning keys way back in the horizon

        scene.add(new THREE.AmbientLight(0xffffff, 0.5));
        const hemiLight = new THREE.HemisphereLight(0xff00ff, 0x00ffff, 0.5);
        scene.add(hemiLight);

        const robotLight = new THREE.PointLight(0xffffff, 1.4, 100);
        robotLight.position.set(0, 25, -60);
        scene.add(robotLight);

        // --- SYNTHWAVE LANDSCAPE & DECORATIONS ---

        function createSunTexture() {
            const canvas = document.createElement('canvas');
            canvas.width = 1024;
            canvas.height = 1024;
            const ctx = canvas.getContext('2d')!;

            // Smooth clean gradient, no black/pink horizontal stripes carving it up!
            const grad = ctx.createLinearGradient(0, 0, 0, 1024);
            grad.addColorStop(0, '#ffeb3b');
            grad.addColorStop(0.5, '#ff9800');
            grad.addColorStop(1, '#e91e63');

            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, 1024, 1024);
            return new THREE.CanvasTexture(canvas);
        }

        // PERFECT CIRCLE SUN
        const sunGeo = new THREE.CircleGeometry(85, 64);
        const sunMat = new THREE.MeshBasicMaterial({
            map: createSunTexture(),
            transparent: true,
            fog: false,
            color: 0xffffff,
            depthWrite: false
        });
        const sun = new THREE.Mesh(sunGeo, sunMat);
        sun.position.set(0, 65, -280);
        scene.add(sun);

        // --- RESTORED: INTERACTIVE HIGH COLUMNS ---
        const bars: any[] = [];
        const barGeo = new THREE.BoxGeometry(3, 1, 3);
        const barColors = [0xff007f, 0x00f3ff, 0xff00ff, 0x9900ff];
        for (let i = -16; i <= 16; i++) {
            if (Math.abs(i) < 4) continue; // Leaves a perfect center gap for the Robot

            const col = barColors[Math.abs(i) % barColors.length];
            const barMat = new THREE.MeshStandardMaterial({ color: col, emissive: col, emissiveIntensity: 1.2 });
            const bar = new THREE.Mesh(barGeo, barMat);

            bar.position.set(i * 4.5, 0, -130); // Positioned perfectly behind the robot
            scene.add(bar);
            bars.push({ mesh: bar, index: i });
        }


        // --- TRUE DENSE CITY SKYLINE ---
        const cityGroup = new THREE.Group();
        const matBuildingSolid = new THREE.MeshBasicMaterial({ color: 0x050011 });
        const matEdgePink = new THREE.LineBasicMaterial({ color: 0xff007f });
        const matEdgeCyan = new THREE.LineBasicMaterial({ color: 0x00f3ff });

        const zLayers = [-90, -120, -150, -180, -210];
        zLayers.forEach((zPos, layerIndex) => {
            for (let x = -200; x <= 200; x += 12) {
                if (Math.random() > 0.85) continue; // Dense packing

                const distFromCenter = Math.abs(x);
                // Clear the middle entirely for the track, robot, and sun framing
                if (distFromCenter < 40) continue;

                // Build heights forming a valley
                let bh = 15 + Math.random() * 20;
                if (distFromCenter > 50) {
                    bh += (distFromCenter - 50) * 0.3 + Math.random() * 20;
                }
                bh += layerIndex * 6; // Back layers are taller
                if (bh > 75) bh = 75;

                const bw = 10 + Math.random() * 10;
                const bd = 10 + Math.random() * 10;

                const bgGeo = new THREE.BoxGeometry(bw, bh, bd);
                const bMesh = new THREE.Mesh(bgGeo, matBuildingSolid);

                const finalX = x + (Math.random() - 0.5) * 6;
                bMesh.position.set(finalX, bh / 2 - 2, zPos + (Math.random() - 0.5) * 10);

                const edgeMat = Math.random() > 0.5 ? matEdgePink : matEdgeCyan;
                const edges = new THREE.EdgesGeometry(bgGeo);
                const line = new THREE.LineSegments(edges, edgeMat);
                line.position.copy(bMesh.position);

                cityGroup.add(bMesh);
                cityGroup.add(line);
            }
        });
        scene.add(cityGroup);

        // Moving Neon Grid Landscape
        const gridGeo = new THREE.PlaneGeometry(600, 600, 60, 60);
        const gridMat = new THREE.MeshBasicMaterial({ color: 0x00ffff, wireframe: true, transparent: true, opacity: 0.15 });
        const grid = new THREE.Mesh(gridGeo, gridMat);
        grid.rotation.x = -Math.PI / 2;
        grid.position.y = -1;
        scene.add(grid);

        // Starfield
        const starGeo = new THREE.BufferGeometry();
        const starVertices = [];
        for (let i = 0; i < 800; i++) {
            starVertices.push((Math.random() - 0.5) * 400, Math.random() * 200 + 30, -Math.random() * 300 - 50);
        }
        starGeo.setAttribute('position', new THREE.Float32BufferAttribute(starVertices, 3));
        const starMat = new THREE.PointsMaterial({ color: 0xffffff, size: 0.5, transparent: true, opacity: 0.8 });
        const stars = new THREE.Points(starGeo, starMat);
        scene.add(stars);

        // --- ASSET GENERATORS ---
        function createTileTexture(letter: string) {
            const canvas = document.createElement('canvas');
            canvas.width = 128;
            canvas.height = 128;
            const ctx = canvas.getContext('2d')!;

            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, 128, 128);

            ctx.strokeStyle = '#222';
            ctx.lineWidth = 6;
            ctx.strokeRect(3, 3, 122, 122);

            ctx.fillStyle = '#000000';
            ctx.font = '900 70px "Arial Black", sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(letter, 64, 66);

            return new THREE.CanvasTexture(canvas);
        }

        function createCheckerTexture() {
            const canvas = document.createElement('canvas');
            canvas.width = 512;
            canvas.height = 512;
            const ctx = canvas.getContext('2d')!;
            const cols = 8;
            const rows = 8;
            const w = canvas.width / cols;
            const h = canvas.height / rows;

            for (let i = 0; i < cols; i++) {
                for (let j = 0; j < rows; j++) {
                    ctx.fillStyle = (i + j) % 2 === 0 ? '#38006b' : '#1a0033';
                    ctx.fillRect(i * w, j * h, w, h);
                }
            }
            const tex = new THREE.CanvasTexture(canvas);
            tex.wrapS = THREE.RepeatWrapping;
            tex.wrapT = THREE.RepeatWrapping;
            tex.repeat.set(3, 20);
            return tex;
        }

        function createTextSprite(message: string, color: string) {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d')!;
            ctx.font = "900 70px sans-serif";
            const metrics = ctx.measureText(message);
            canvas.width = metrics.width + 40;
            canvas.height = 100;

            ctx.font = "900 70px sans-serif";
            ctx.fillStyle = color;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(message, canvas.width / 2, canvas.height / 2);

            const texture = new THREE.CanvasTexture(canvas);
            const spriteMat = new THREE.SpriteMaterial({ map: texture, transparent: true });
            const sprite = new THREE.Sprite(spriteMat);
            sprite.scale.set(canvas.width / 25, canvas.height / 25, 1);
            return sprite;
        }

        // --- TRACK OBJECTS ---

        const floorGeo = new THREE.PlaneGeometry(22, 400);
        const floorTexture = createCheckerTexture();
        const floorMat = new THREE.MeshStandardMaterial({
            map: floorTexture,
            roughness: 0.8,
            metalness: 0.1
        });
        const floor = new THREE.Mesh(floorGeo, floorMat);
        floor.rotation.x = -Math.PI / 2;
        floor.position.z = -100;
        scene.add(floor);

        // Floating Track Rails
        const leftCubesGroup = new THREE.Group();
        const rightCubesGroup = new THREE.Group();

        const railCubeGeo = new THREE.BoxGeometry(1.2, 1.2, 1.2);

        const matPinkSolid = new THREE.MeshStandardMaterial({ color: 0xff007f, emissive: 0xff007f, emissiveIntensity: 1.0 });
        const matCyanSolid = new THREE.MeshStandardMaterial({ color: 0x00f3ff, emissive: 0x00f3ff, emissiveIntensity: 1.0 });
        const matPinkGlass = new THREE.MeshStandardMaterial({ color: 0xff007f, emissive: 0xff007f, emissiveIntensity: 0.4, transparent: true, opacity: 0.4 });
        const matCyanGlass = new THREE.MeshStandardMaterial({ color: 0x00f3ff, emissive: 0x00f3ff, emissiveIntensity: 0.4, transparent: true, opacity: 0.4 });
        const cubeMats = [matPinkSolid, matCyanSolid, matPinkGlass, matCyanGlass];

        for (let z = 10; z >= -220; z -= 3.5) {
            const mLeft = cubeMats[Math.floor(Math.random() * cubeMats.length)];
            const cLeft = new THREE.Mesh(railCubeGeo, mLeft);
            cLeft.position.set(0, 0.6, z);
            leftCubesGroup.add(cLeft);
            animatedCubes.push({ mesh: cLeft });

            const mRight = cubeMats[Math.floor(Math.random() * cubeMats.length)];
            const cRight = new THREE.Mesh(railCubeGeo, mRight);
            cRight.position.set(0, 0.6, z);
            rightCubesGroup.add(cRight);
            animatedCubes.push({ mesh: cRight });
        }

        leftCubesGroup.position.x = -11 - 1.2;
        rightCubesGroup.position.x = 11 + 1.2;
        scene.add(leftCubesGroup, rightCubesGroup);

        const hitLineGeo = new THREE.PlaneGeometry(22, 1.5);
        const hitLineMat = new THREE.MeshBasicMaterial({ color: 0xff0055, transparent: true, opacity: 0.4, side: THREE.DoubleSide });
        const hitLine = new THREE.Mesh(hitLineGeo, hitLineMat);
        hitLine.rotation.x = -Math.PI / 2;
        hitLine.position.set(0, 0.05, HIT_Z);
        scene.add(hitLine);

        // --- 3D CHARACTER MODEL ---
        let mixer: any;
        let robotModel: any;

        const gltfLoader = new THREE.GLTFLoader();
        gltfLoader.load('https://raw.githubusercontent.com/mrdoob/three.js/master/examples/models/gltf/RobotExpressive/RobotExpressive.glb', (gltf: any) => {
            robotModel = gltf.scene;
            robotModel.position.set(0, 0, -80);
            robotModel.scale.set(6.0, 6.0, 6.0);
            robotModel.rotation.y = 0;
            scene.add(robotModel);

            mixer = new THREE.AnimationMixer(robotModel);
            const danceClip = gltf.animations.find((clip: any) => clip.name === 'Dance');
            if (danceClip) {
                const action = mixer.clipAction(danceClip);
                action.play();
            }
        });

        // --- GAMEPLAY LOGIC ---
        function getFingerForLane(laneIndex: number, mode: 'left' | 'right' | 'both'): string {
            if (mode === 'left') {
                const fingers = ['leftPinky', 'leftRing', 'leftMiddle', 'leftPointing', 'leftThumb'];
                return fingers[laneIndex];
            } else if (mode === 'right') {
                const fingers = ['rightThumb', 'rightPointing', 'rightMiddle', 'rightRing', 'rightPinky'];
                return fingers[laneIndex];
            } else {
                const fingers = [
                    'leftPinky', 'leftRing', 'leftMiddle', 'leftPointing', 'leftThumb',
                    'rightThumb', 'rightPointing', 'rightMiddle', 'rightRing', 'rightPinky'
                ];
                return fingers[laneIndex];
            }
        }

        function spawnTile() {
            const laneIndex = Math.floor(Math.random() * currentLanes);

            // Map lane to correct biological finger keys!
            const fingerId = getFingerForLane(laneIndex, currentMode);
            const availableKeys = FINGER_KEY_MAPPING[fingerId as keyof typeof FINGER_KEY_MAPPING];
            const validKeys = availableKeys.map(k => k.toUpperCase()).filter(k => /^[A-Z]$/.test(k));

            let char = 'A';
            if (validKeys.length > 0) {
                char = validKeys[Math.floor(Math.random() * validKeys.length)];
            }

            const laneWidth = (currentTrackWidth - 2) / currentLanes;
            const xPos = (laneIndex * laneWidth) - (currentTrackWidth / 2) + (laneWidth / 2) + 1;

            // THICK 3D PHYSICAL KEYS (Top is letter, sides are dark plastic)
            const matTop = new THREE.MeshBasicMaterial({ map: createTileTexture(char) });
            const matSide = new THREE.MeshStandardMaterial({ color: 0x111111, roughness: 0.9 });
            const materials = [matSide, matSide, matTop, matSide, matSide, matSide]; // Mapping faces

            // Thickened (1.2 height) and raised (0.6 y) to perfectly match rail cubes
            const mesh = new THREE.Mesh(new THREE.BoxGeometry(2.8, 1.2, 2.8), materials);
            mesh.position.set(xPos, 0.6, SPAWN_Z);
            scene.add(mesh);

            tiles.push({ mesh, char, active: true });
        }

        const onHit = (pts: number) => {
            score += pts;
            combo++;
            setUiState({ score, combo });
            playTone(pts >= 100 ? 880 : 550, 'sine', 0.1, 0.15);
        };

        const onMiss = (pos?: any) => {
            combo = 0;
            setUiState({ score, combo });
            playTone(150, 'sawtooth', 0.2, 0.2);

            if (pos) {
                const sprite = createTextSprite("MISS", "#ff0000");
                sprite.position.copy(pos);
                sprite.position.y += 2.5;
                scene.add(sprite);
                floatingTexts.push({ sprite, life: 1.0, velocity: new THREE.Vector3(0, 0.05, 0) });
            }
        };

        function createExplosion(pos: any) {
            for (let i = 0; i < 15; i++) {
                const geo = new THREE.BoxGeometry(0.8, 0.8, 0.8);
                const mat = new THREE.MeshBasicMaterial({ color: Math.random() > 0.5 ? 0xff00ff : 0x00ffff });
                const p = new THREE.Mesh(geo, mat);
                p.position.copy(pos);
                scene.add(p);
                particles.push({
                    mesh: p,
                    velocity: new THREE.Vector3((Math.random() - 0.5) * 20, Math.random() * 15 + 5, (Math.random() - 0.5) * 20),
                    life: 1.0
                });
            }
        }

        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isPlaying || !gameActive) return;
            const key = e.key.toUpperCase();

            if (key.length > 1 || !/[A-Z]/.test(key)) return;

            hitLineMat.opacity = 0.9;
            setTimeout(() => { if (hitLineMat) hitLineMat.opacity = 0.4; }, 100);

            let bestTile = null;
            let minZDist = Infinity;

            for (let i = 0; i < tiles.length; i++) {
                const t = tiles[i];
                if (t.active && t.char === key) {
                    const zDist = Math.abs(t.mesh.position.z - HIT_Z);
                    if (zDist < 8.0 && zDist < minZDist) {
                        minZDist = zDist;
                        bestTile = t;
                    }
                }
            }

            if (bestTile) {
                bestTile.active = false;
                scene.remove(bestTile.mesh);
                createExplosion(bestTile.mesh.position);

                let pts = 50;
                if (minZDist < 2.5) pts = 200;
                else if (minZDist < 5.0) pts = 100;

                const sprite = createTextSprite(pts.toString(), pts === 200 ? "#ff00ff" : "#ffffff");
                sprite.position.copy(bestTile.mesh.position);
                sprite.position.y += 2.5;
                scene.add(sprite);
                floatingTexts.push({ sprite, life: 1.0, velocity: new THREE.Vector3(0, 0.08, 0) });

                onHit(pts);
            } else {
                onMiss();
            }
        };

        window.addEventListener('keydown', handleKeyDown);


        // --- MAIN RENDER LOOP ---
        const clock = new THREE.Clock();
        let lastSpawnTime = 0;
        let animationFrameId: number;

        function animate() {
            animationFrameId = requestAnimationFrame(animate);

            const dt = clock.getDelta();
            const time = clock.getElapsedTime();

            if (!isPlaying) return;

            analyser.getByteFrequencyData(dataArray);

            floorTexture.offset.y -= dt * (currentFallSpeed / 15);

            // Animate restored High Columns based on live EQ data
            bars.forEach(b => {
                const index = Math.abs(b.index);
                const freqValue = dataArray[index] || 0;
                const targetH = 2 + (freqValue / 6); // Dramatic scaling based on beat
                b.mesh.scale.y += (targetH - b.mesh.scale.y) * 0.2;
                b.mesh.position.y = b.mesh.scale.y / 2;
            });

            if (gameActive) {
                const bassEnergy = (dataArray[0] + dataArray[1] + dataArray[2]) / 3;
                const isAudioPlaying = bassEnergy > 10;

                if (isAudioPlaying) {
                    // Spawn logic
                    if (bassEnergy > currentSpawnEnergyThreshold && time > lastSpawnTime + currentSpawnCooldown) {
                        lastSpawnTime = time;
                        spawnTile();

                        if (bassEnergy > currentSpawnEnergyThreshold + 35 && Math.random() > 0.6) {
                            setTimeout(spawnTile, currentSpawnCooldown * 500);
                        }

                        if (robotModel) robotModel.position.y = 8.0;

                        // Randomly swap textures on heavy beat
                        if (Math.random() > 0.4) {
                            for (let i = 0; i < 15; i++) {
                                const randomCube = animatedCubes[Math.floor(Math.random() * animatedCubes.length)];
                                randomCube.mesh.material = cubeMats[Math.floor(Math.random() * cubeMats.length)];
                            }
                        }
                    }
                } else {
                    if (time > lastSpawnTime + currentSpawnCooldown * 1.5) {
                        lastSpawnTime = time;
                        spawnTile();
                        if (robotModel) robotModel.position.y = 8.0;
                    }
                }
            }

            // Recover robot
            if (robotModel) {
                robotModel.position.y += (0 - robotModel.position.y) * 0.1;
            }
            if (mixer) {
                mixer.update(dt);
            }

            grid.position.z = (grid.position.z + dt * 10) % 10;
            stars.rotation.y += dt * 0.02;

            for (let i = tiles.length - 1; i >= 0; i--) {
                const t = tiles[i];
                if (t.active) {
                    t.mesh.position.z += dt * currentFallSpeed;

                    if (t.mesh.position.z > HIT_Z + 6.0) {
                        t.active = false;
                        scene.remove(t.mesh);
                        tiles.splice(i, 1);
                        onMiss(t.mesh.position);
                    }
                } else {
                    scene.remove(t.mesh);
                    tiles.splice(i, 1);
                }
            }

            for (let i = floatingTexts.length - 1; i >= 0; i--) {
                const ft = floatingTexts[i];
                ft.sprite.position.add(ft.velocity);
                ft.life -= dt * 1.2;
                ft.sprite.material.opacity = ft.life;
                if (ft.life <= 0) {
                    scene.remove(ft.sprite);
                    floatingTexts.splice(i, 1);
                }
            }

            for (let i = particles.length - 1; i >= 0; i--) {
                const p = particles[i];
                p.mesh.position.addScaledVector(p.velocity, dt);
                p.velocity.y -= 40 * dt;
                p.life -= dt * 1.5;
                p.mesh.scale.setScalar(Math.max(0, p.life));

                if (p.life <= 0) {
                    scene.remove(p.mesh);
                    particles.splice(i, 1);
                }
            }

            renderer.render(scene, camera);
        }

        animate();

        const handleResize = () => {
            camera.aspect = window.innerWidth / (window.innerHeight - 100);
            camera.updateProjectionMatrix();
            renderer.setSize(window.innerWidth, window.innerHeight - 100);
        };
        window.addEventListener('resize', handleResize);

        return () => {
            isPlaying = false;
            if (audioSourceNode) {
                try { audioSourceNode.stop(); } catch (e) { }
                audioSourceNode.disconnect();
            }
            window.removeEventListener('START_GAME', startGameHandler);
            window.removeEventListener('STOP_GAME', stopGameHandler);
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('resize', handleResize);
            cancelAnimationFrame(animationFrameId);
            renderer.dispose();
            if (audioCtx.state !== 'closed') audioCtx.close();
        };

    }, [isLoaded]);

    const cycleHandMode = () => {
        setHandMode(prev => prev === 'left' ? 'right' : prev === 'right' ? 'both' : 'left');
    };

    const cycleDifficulty = () => {
        setDifficulty(prev => prev === 'easy' ? 'medium' : prev === 'medium' ? 'hard' : 'easy');
    };

    return (
        <div className="gh-container">
            <div ref={mountRef} className="gh-canvas" />

            <div className="gh-ui-layer">
                <div className={`gh-controls-left ${gameState === 'playing' ? 'gh-hidden-ui' : ''}`}>
                    <div onClick={cycleHandMode} className="gh-circle-btn gh-border-cyan">
                        <div className="gh-circle-text">
                            {handMode === 'left' ? 'Left\nHand' : handMode === 'right' ? 'Right\nHand' : 'Two\nHands'}
                        </div>
                    </div>
                    <div onClick={cycleDifficulty} className="gh-circle-btn gh-border-pink">
                        <div className="gh-circle-text">{difficulty}</div>
                    </div>
                </div>

                <div className={`gh-controls-right ${gameState === 'playing' ? 'gh-hidden-ui' : ''}`}>
                    <button
                        disabled={!isMusicLoaded}
                        onClick={() => {
                            setGameState('playing');
                            window.dispatchEvent(new CustomEvent('START_GAME', {
                                detail: { handMode, difficulty }
                            }));
                        }}
                        className={`gh-action-btn gh-play-btn ${!isMusicLoaded ? 'gh-disabled' : ''}`}
                    >
                        {isMusicLoaded ? 'Play It!' : 'Loading Beat...'}
                    </button>
                    <button className="gh-action-btn gh-share-btn">Share It!</button>
                    <button className="gh-action-btn gh-love-btn">Love It!</button>
                </div>

                <div className={`gh-middle-layer ${gameState === 'menu' ? 'gh-hidden-ui' : ''}`}>
                    <div className="gh-score-area">
                        <div className="gh-score-text">score: {uiState.score}</div>
                        <div
                            onClick={() => {
                                setGameState('menu');
                                window.dispatchEvent(new Event('STOP_GAME'));
                            }}
                            className="gh-back-btn"
                        >
                            &lt; GO BACK TO MENU
                        </div>
                    </div>

                    <div className="gh-combo-area">
                        <div className="gh-combo-circle">
                            <div className="gh-combo-value">X{uiState.combo}</div>
                            <div className="gh-combo-label">combo</div>
                        </div>
                    </div>
                </div>

                <div className="gh-bottom-text">NOT CLICK. THE FLOATING L...</div>
            </div>

            {!isLoaded && (
                <div className="gh-loading">INITIALIZING ENGINE...</div>
            )}

            <style>{`
                .gh-container {
                    position: relative;
                    width: 100%;
                    height: calc(100vh - 100px); /* Height underneath common header */
                    background-color: #0a0014;
                    overflow: hidden;
                    font-family: sans-serif;
                    user-select: none;
                }
                .gh-canvas {
                    position: absolute;
                    inset: 0;
                    width: 100%;
                    height: 100%;
                }
                .gh-ui-layer {
                    position: absolute;
                    inset: 0;
                    pointer-events: none;
                    padding: 2rem;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    z-index: 10;
                }
                .gh-hidden-ui {
                    opacity: 0 !important;
                    pointer-events: none !important;
                }
                .gh-controls-left {
                    position: absolute;
                    top: 2rem;
                    left: 2rem;
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                    transition: opacity 0.5s;
                    opacity: 1;
                }
                .gh-circle-btn {
                    width: 6rem;
                    height: 6rem;
                    border-radius: 50%;
                    border: 4px solid;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background-color: rgba(0, 0, 0, 0.6);
                    backdrop-filter: blur(12px);
                    cursor: pointer;
                    pointer-events: auto;
                    transition: background-color 0.2s;
                }
                .gh-circle-btn:hover {
                    background-color: rgba(255, 255, 255, 0.1);
                }
                .gh-border-cyan {
                    border-color: #00ccff;
                    box-shadow: 0 0 15px rgba(0,204,255,0.5);
                }
                .gh-border-pink {
                    border-color: #ff0055;
                    box-shadow: 0 0 15px rgba(255,0,85,0.5);
                }
                .gh-circle-text {
                    color: white;
                    font-size: 0.75rem;
                    font-weight: 900;
                    text-transform: uppercase;
                    letter-spacing: 0.1em;
                    text-align: center;
                    white-space: pre-line;
                    line-height: 1.2;
                }
                .gh-controls-right {
                    display: flex;
                    justify-content: flex-end;
                    gap: 1.25rem;
                    margin-top: 0.5rem;
                    margin-right: 0.5rem;
                    transition: opacity 0.5s;
                    opacity: 1;
                }
                .gh-action-btn {
                    color: white;
                    font-weight: 800;
                    padding: 0.5rem 1.5rem;
                    border-radius: 0.375rem;
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
                    font-size: 0.875rem;
                    pointer-events: auto;
                    transition: transform 0.2s;
                    cursor: pointer;
                }
                .gh-action-btn:hover:not(.gh-disabled) {
                    transform: scale(1.05);
                }
                .gh-play-btn {
                    background-color: #ff0055;
                    transform: rotate(2deg);
                }
                .gh-play-btn:hover:not(.gh-disabled) {
                    transform: rotate(2deg) scale(1.05);
                }
                .gh-play-btn.gh-disabled {
                    opacity: 0.5;
                    cursor: wait;
                }
                .gh-share-btn {
                    background-color: #00ccff;
                    transform: rotate(-1deg);
                }
                .gh-share-btn:hover {
                    transform: rotate(-1deg) scale(1.05);
                }
                .gh-love-btn {
                    background-color: #9333ea;
                    transform: rotate(3deg);
                }
                .gh-love-btn:hover {
                    transform: rotate(3deg) scale(1.05);
                }
                @media (max-width: 640px) {
                    .gh-love-btn { display: none; }
                }
                .gh-middle-layer {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-top: 6rem;
                    transition: opacity 0.5s;
                    opacity: 1;
                }
                .gh-score-area {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 1rem;
                    margin-left: 3rem;
                }
                .gh-score-text {
                    color: white;
                    font-weight: 900;
                    font-size: 3.75rem;
                    letter-spacing: 0.1em;
                    filter: drop-shadow(0 0 15px rgba(255,0,255,0.8));
                }
                .gh-back-btn {
                    color: white;
                    font-size: 0.75rem;
                    letter-spacing: 0.25em;
                    font-weight: 700;
                    background-color: rgba(0, 0, 0, 0.6);
                    padding: 0.5rem 1rem;
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    cursor: pointer;
                    pointer-events: auto;
                    transition: all 0.2s;
                    border-radius: 0.125rem;
                    backdrop-filter: blur(4px);
                }
                .gh-back-btn:hover {
                    background-color: white;
                    color: black;
                }
                .gh-combo-area {
                    margin-right: 5rem;
                    transform: rotate(8deg);
                }
                .gh-combo-circle {
                    width: 15rem;
                    height: 15rem;
                    border-radius: 50%;
                    border: 14px solid white;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    background-color: rgba(0, 0, 0, 0.8);
                    backdrop-filter: blur(12px);
                    box-shadow: 0 0 40px rgba(255,255,255,0.5), inset 0 0 30px rgba(255,255,255,0.3);
                }
                .gh-combo-value {
                    color: white;
                    font-weight: 900;
                    font-size: 6rem;
                    margin-top: 1.25rem;
                    filter: drop-shadow(0 4px 3px rgba(0,0,0,0.07));
                }
                .gh-combo-label {
                    color: white;
                    font-weight: 800;
                    font-size: 1.5rem;
                    letter-spacing: 0.4em;
                    margin-top: -0.625rem;
                    text-transform: uppercase;
                }
                .gh-bottom-text {
                    width: 100%;
                    text-align: center;
                    padding-bottom: 1rem;
                    color: rgba(255, 255, 255, 0.3);
                    font-size: 0.75rem;
                    font-family: monospace;
                    letter-spacing: 0.4em;
                    text-transform: uppercase;
                }
                .gh-loading {
                    position: absolute;
                    inset: 0;
                    background-color: #0a0014;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 50;
                    color: white;
                    font-size: 1.875rem;
                    font-family: monospace;
                    letter-spacing: 0.3em;
                    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
                }
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }
            `}</style>
        </div>
    );
}